import { useEffect } from "react";
import { Link } from "react-router-dom";
import { IconClose } from "./icons";
import styles from "./AddToCartToast.module.css";

// "Just added" toast. Auto-dismisses after `autoCloseMs` (default 8s) but
// stays put while hovered so it doesn't yank away mid-read.
//
// Shape:
//   item = {
//     name, image, sku, qty, price, color: { name, hex, image }, size
//   }
export default function AddToCartToast({ open, item, onClose, formatPrice, autoCloseMs = 8000 }) {
  useEffect(() => {
    if (!open) return;
    if (!autoCloseMs) return;
    const t = setTimeout(() => onClose?.(), autoCloseMs);
    return () => clearTimeout(t);
  }, [open, item, autoCloseMs, onClose]);

  return (
    <div
      className={[styles.wrap, open ? styles.open : ""].join(" ")}
      role="status"
      aria-live="polite"
    >
      {item && (
        <div className={styles.card}>
          <div className={styles.head}>
            <div className={styles.headLeft}>
              <span
                className={[styles.check, item.capped ? styles.checkWarn : ""].join(" ")}
                aria-hidden
              >
                {item.capped ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12" y2="17.01" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="4 12 10 18 20 6" />
                  </svg>
                )}
              </span>
              <span className={styles.headTitle}>
                {item.capped
                  ? (item.qty > 0 ? "Capped at stock" : "No stock available")
                  : "Added to Cart"}
              </span>
            </div>
            <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
              <IconClose size={18} />
            </button>
          </div>

          <div className={styles.body}>
            <div className={styles.thumbWrap}>
              {item.image ? (
                <img src={item.image} alt="" className={styles.thumb} />
              ) : (
                <div className={styles.thumbPlaceholder} />
              )}
            </div>
            <div className={styles.details}>
              <div className={styles.name}>{item.name}</div>
              {item.price != null && (
                <div className={styles.price}>
                  {formatPrice ? formatPrice(item.price) : `$${Number(item.price).toFixed(2)}`}
                </div>
              )}
              <div className={styles.line}><span>SKU:</span> {item.sku}</div>
              {item.color && (
                <div className={styles.line}>
                  <span>Color:</span>
                  <span className={styles.swatch} style={swatchStyle(item.color)} aria-hidden />
                  {item.color.name}
                </div>
              )}
              {item.size && <div className={styles.line}><span>Size:</span> {item.size}</div>}
              <div className={styles.line}><span>Quantity:</span> {item.qty}</div>
              {item.capped && typeof item.stock === "number" && (
                <div className={styles.warn}>
                  Only {item.stock} {item.stock === 1 ? "unit" : "units"} available — adjust qty in your cart.
                </div>
              )}
            </div>
          </div>

          <div className={styles.actions}>
            <Link to="/cart" className={styles.cta} onClick={onClose}>
              View Cart & Checkout →
            </Link>
            <button type="button" className={styles.continue} onClick={onClose}>
              Continue Shopping
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function swatchStyle(c) {
  if (!c) return { background: "#ccc" };
  if (c.image) return { backgroundImage: `url(${c.image})`, backgroundSize: "cover" };
  return { background: c.hex || "#ccc" };
}
